import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Reveal from './Reveal';
import { Pen, Wash, PenCanvas, Person, HandArrow, sketch } from './HowDrawings';
import './TileAssembly.css';
import './OnTheDay.css';

// The end of the "how it works" page, and the heart of the idea: the day
// itself. The organiser carries in the empty board and hangs it up; the
// makers stand by, each holding the canvas they made, and one by one the
// canvases fly up into the board until it is full - one Mosaik. Then, and
// only then, the way on: the blue "start your Mosaik" stamp of the homepage.
//
// Drawn with the same pen as the rest of the page (components/HowDrawings.js)
// and moved the way the homepage hero is (components/TileAssembly.js): the
// section is taller than the screen, the stage inside it is sticky, and the
// scroll position - smoothed - decides every frame. Visitors who asked for
// less motion get the finished picture, standing still.

const TILE = 52;
const GAP = 6;
const PAD = 10;
const COLS = 4;
const ROWS = 3;
const BW = PAD * 2 + COLS * TILE + (COLS - 1) * GAP;
const BH = PAD * 2 + ROWS * TILE + (ROWS - 1) * GAP;

// Two stagings of the same scene: side by side on a wide screen, stacked on
// a phone. Makers are [x, y, scale, hair]; their canvas is held at the chest.
const LAYOUTS = {
  wide: {
    w: 900,
    h: 600,
    board: [327, 64],
    organiser: [120, 380, 1.9],
    makers: [
      [310, 400, 1.35, 'long'], [420, 400, 1.35, 'cap'], [530, 400, 1.35, 'bun'],
      [640, 400, 1.35], [750, 400, 1.35, 'tuft'], [860, 400, 1.35, 'long'],
      [255, 444, 1.5, 'bun'], [365, 444, 1.5], [475, 444, 1.5, 'tuft'],
      [585, 444, 1.5, 'long'], [695, 444, 1.5, 'cap'], [805, 444, 1.5, 'bun'],
    ],
    organiserLabel: [120, 566],
    makersLabel: [560, 576],
    font: 22,
    washes: [[120, 480, 110, 46], [560, 512, 340, 52]],
  },
  // Narrow, so that on a phone the people are drawn big enough to read.
  tall: {
    w: 420,
    h: 790,
    board: [87, 64],
    organiser: [210, 352, 1.75],
    makers: [
      [60, 540, 1.2, 'long'], [160, 540, 1.2, 'cap'], [260, 540, 1.2, 'bun'], [360, 540, 1.2],
      [110, 596, 1.3, 'tuft'], [210, 596, 1.3, 'long'], [310, 596, 1.3], [370, 650, 1.4, 'bun'],
      [60, 650, 1.4, 'cap'], [160, 650, 1.4, 'bun'], [265, 650, 1.4, 'tuft'], [10, 596, 1.3, 'long'],
    ],
    organiserLabel: [210, 498],
    makersLabel: [210, 768],
    font: 24,
    washes: [[210, 430, 110, 46], [210, 660, 200, 76]],
  },
};

// What each maker made, how they hold it, which spot it goes to on the
// board, and in which order they come up.
const MARKS = ['heart', 'wave', 'dot', 'fill', 'lines', 'sun', 'hill', 'cross', 'circle', 'wave', 'dot', 'heart'];
const HELD_TILT = [-6, 4, -3, 7, -5, 3, 6, -4, 2, -7, 5, -2];
const SLOT = [5, 0, 10, 3, 7, 9, 1, 11, 4, 2, 8, 6];
const TURN = [3, 8, 0, 10, 5, 1, 11, 6, 2, 9, 4, 7];
// The one canvas that never quite hangs straight - like the logo.
const LOOSE_SLOT = 3;

// The script, as shares of the scroll.
const BOARD_FROM = 0.04;
const BOARD_FOR = 0.24;
const STRING_FROM = 0.27;
const TILES_FROM = 0.33;
const TILE_STAGGER = 0.034;
const TILE_FOR = 0.15;
const DONE_FROM = 0.86;

const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const smoothstep = (a, b, v) => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const lerp = (a, b, t) => a + (b - a) * t;
// A point on the curve from a to b that bows up through the control point.
const arc = (a, ctrl, b, t) => (1 - t) ** 2 * a + 2 * (1 - t) * t * ctrl + t ** 2 * b;

function geometry(layout) {
  const [bx, by] = layout.board;
  const [ox, oy, os] = layout.organiser;
  // The board starts small in the organiser's hands, at their chest.
  const held = { x: ox - BW * 0.25, y: oy + 38 * os - BH * 0.25 };
  const tiles = layout.makers.map(([x, y, s], i) => {
    const slot = SLOT[i];
    const loose = slot === LOOSE_SLOT;
    const ex = bx + PAD + (slot % COLS) * (TILE + GAP) + TILE / 2 + (loose ? 3 : 0);
    const ey = by + PAD + Math.floor(slot / COLS) * (TILE + GAP) + TILE / 2 - (loose ? 4 : 0);
    const sx = x;
    const sy = y + 38 * s;
    return {
      sx,
      sy,
      ex,
      ey,
      cx: (sx + ex) / 2 + (sx < ex ? -40 : 40),
      cy: Math.min(sy, ey) - 110,
      k0: (24 * s) / TILE,
      r0: HELD_TILT[i],
      r1: loose ? 12 : 0,
      start: TILES_FROM + TURN[i] * TILE_STAGGER,
    };
  });
  return { bx, by, held, tiles };
}

// Where the blue stamp sits in the wide picture, in its units: the middle of
// it, and its size. (The picture is 900 × 600; the board ends at x = 573.)
const STAMP = { x: 774, y: 178, w: 224, h: 136 };

// The blue "start your Mosaik" stamp from the homepage (TileAssembly.js,
// same styles), for the phone layout, where it goes below the picture.
function Stamp({ className }) {
  const { t } = useTranslation();
  return (
    <div className={`od-cta ${className}`}>
      <svg className="ta-defs" aria-hidden="true" focusable="false">
        <filter id="ta-rough">
          <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" seed="4" />
          <feDisplacementMap in="SourceGraphic" scale="8" />
        </filter>
      </svg>
      <Link to="/order" className="ta-stamp od-stamp">
        <span className="ta-stamp-bg" aria-hidden="true" />
        <span className="ta-stamp-side ta-stamp-side--left" aria-hidden="true">
          {t('home.hero.stampLeft')}
        </span>
        <span className="ta-stamp-main">
          {t('home.cta')}
          <span className="ta-stamp-arrow" aria-hidden="true">&rarr;</span>
        </span>
        <span className="ta-stamp-side ta-stamp-side--right" aria-hidden="true">
          {t('home.hero.stampRight')}
        </span>
      </Link>
    </div>
  );
}

function OnTheDay() {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const boardRef = useRef(null);
  const handsRef = useRef(null);
  const stringRef = useRef(null);
  const nailRef = useRef(null);
  const tileRefs = useRef([]);
  const doneRef = useRef(null);
  const resultRef = useRef(null);
  const stageRef = useRef(null);
  const hintRef = useRef(null);

  const narrowQuery = '(max-width: 700px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(narrowQuery).matches);
  const [reduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const layout = LAYOUTS[narrow ? 'tall' : 'wide'];

  useEffect(() => {
    const query = window.matchMedia(narrowQuery);
    const onChange = () => setNarrow(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Draws the scene as it is at progress p (0 = nothing has happened yet,
  // 1 = the board is full).
  const paintRef = useRef(null);
  paintRef.current = (p) => {
    const geo = geometry(layout);

    const b = ease(clamp((p - BOARD_FROM) / BOARD_FOR));
    const x = arc(geo.held.x, (geo.held.x + geo.bx) / 2 - 60, geo.bx, b);
    const y = arc(geo.held.y, Math.min(geo.held.y, geo.by) - 70, geo.by, b);
    const tilt = -4 * (1 - b) + Math.sin(b * Math.PI) * 6;
    boardRef.current?.setAttribute(
      'transform',
      `translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(${lerp(0.5, 1, b).toFixed(4)}) rotate(${tilt.toFixed(2)} ${BW / 2} ${BH / 2})`
    );
    if (handsRef.current) handsRef.current.style.opacity = (1 - smoothstep(BOARD_FROM, BOARD_FROM + 0.04, p)).toFixed(3);

    const hung = smoothstep(STRING_FROM, STRING_FROM + 0.07, p);
    if (stringRef.current) {
      stringRef.current.style.strokeDashoffset = (1 - hung).toFixed(4);
      stringRef.current.style.visibility = hung > 0.001 ? 'visible' : 'hidden';
    }
    if (nailRef.current) nailRef.current.style.opacity = hung.toFixed(3);

    geo.tiles.forEach((tile, i) => {
      const el = tileRefs.current[i];
      if (!el) return;
      const f = ease(clamp((p - tile.start) / TILE_FOR));
      const lift = Math.sin(f * Math.PI);
      const tx = arc(tile.sx, tile.cx, tile.ex, f);
      const ty = arc(tile.sy, tile.cy, tile.ey, f);
      const rot = lerp(tile.r0, tile.r1, f) + lift * (i % 2 ? 22 : -22);
      const k = lerp(tile.k0, 1, f) + lift * 0.18;
      el.setAttribute('transform', `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) rotate(${rot.toFixed(2)}) scale(${k.toFixed(4)})`);
    });

    const done = smoothstep(DONE_FROM, DONE_FROM + 0.09, p);
    if (doneRef.current) {
      doneRef.current.style.opacity = done.toFixed(3);
      doneRef.current.style.setProperty('--pop', done.toFixed(3));
    }
    if (resultRef.current) resultRef.current.style.opacity = done.toFixed(3);
    // The way on, once there is something to show for it.
    const next = smoothstep(DONE_FROM + 0.04, DONE_FROM + 0.12, p);
    // "Scroll" under the title, gone as soon as the scene gets going.
    if (hintRef.current) hintRef.current.style.opacity = (1 - smoothstep(0.02, 0.1, p)).toFixed(3);

    // One number on the stage; the arrow and the stamp both follow it.
    stageRef.current?.style.setProperty('--next', next.toFixed(3));
    stageRef.current?.style.setProperty('--next-vis', next > 0.02 ? 'visible' : 'hidden');
  };

  // The finished picture for reduced motion; the starting one otherwise
  // (until the loop below takes over).
  useLayoutEffect(() => {
    paintRef.current(reduced ? 1 : 0);
  }, [layout, reduced]);

  useEffect(() => {
    if (reduced) return undefined;
    const section = sectionRef.current;
    if (!section) return undefined;

    let smooth = null;
    let raf = 0;
    let visible = false;

    const tick = () => {
      raf = 0;
      const box = section.getBoundingClientRect();
      const target = clamp(-box.top / Math.max(1, box.height - window.innerHeight));
      smooth = smooth === null ? target : smooth + (target - smooth) * 0.09;
      if (Math.abs(target - smooth) < 0.0004) smooth = target;
      paintRef.current(smooth);
      if (visible) raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    });
    observer.observe(section);
    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [layout, reduced]);

  const { bx, by } = geometry(layout);
  const [ox, oy, os] = layout.organiser;
  const heldY = oy + 38 * os;
  const sparkles = [
    [bx - 30, by + 34, 1],
    [bx + BW + 30, by + 22, 0.8],
    [bx - 18, by + BH - 6, 0.7],
    [bx + BW + 22, by + BH - 34, 1.1],
  ];

  return (
    <>
      <section ref={sectionRef} className={`od ${reduced ? 'od--static' : ''}`} aria-labelledby="od-title">
        <div ref={stageRef} className="od-stage">
          <div className="od-head">
            <Reveal as="p" className="h-label">
              {t('how.day.label')}
            </Reveal>
            <Reveal as="h2" id="od-title" className="h-display hw-subtitle" delay={100}>
              {t('how.day.title')}
            </Reveal>
            {/* Like the homepage hero's: a line that keeps dropping, saying
                the scene is played by scrolling. */}
            {!reduced && (
              <p ref={hintRef} className="od-hint" aria-hidden="true">
                <span className="ta-hint-line" />
                {t('how.day.scrollHint')}
              </p>
            )}
          </div>

          <p className="od-sr">{t('how.day.description')}</p>

          <div className="od-scene" style={{ aspectRatio: `${layout.w} / ${layout.h}` }}>
          <svg
            className="od-svg"
            viewBox={`0 0 ${layout.w} ${layout.h}`}
            aria-hidden="true"
            focusable="false"
          >
            {layout.washes.map(([cx, cy, rx, ry]) => (
              <Wash key={`${cx}-${cy}`} cx={cx} cy={cy} rx={rx} ry={ry} />
            ))}

            <Pen>
              {/* The nail and the string the board hangs from. */}
              <circle ref={nailRef} className="hl-ink" cx={bx + BW / 2} cy={by - 34} r="3.4" />
              <path
                ref={stringRef}
                className="hl hl-thin"
                pathLength="1"
                strokeDasharray="1 1"
                d={`M${bx + 18} ${by + 2} L${bx + BW / 2} ${by - 34} L${bx + BW - 18} ${by + 2}`}
              />

              {layout.makers.map(([x, y, s, hair]) => (
                <g key={`${x}-${y}`} transform={`translate(${x} ${y}) scale(${s})`}>
                  <Person x={0} y={0} hair={hair} />
                </g>
              ))}

              <g transform={`translate(${ox} ${oy}) scale(${os})`}>
                <Person x={0} y={0} hair="tuft" />
              </g>

              {/* The board: empty spots, waiting. */}
              <g ref={boardRef}>
                <rect className="hl-paper" width={BW} height={BH} />
                {Array.from({ length: COLS * ROWS }, (_, i) => (
                  <path
                    key={i}
                    className="hl hl-thin od-spot"
                    d={sketch(PAD + (i % COLS) * (TILE + GAP), PAD + Math.floor(i / COLS) * (TILE + GAP), TILE, TILE, 1)}
                  />
                ))}
                <path className="hl" d={sketch(0, 0, BW, BH)} />
              </g>
              <g ref={handsRef}>
                {[-1, 1].map((side) => (
                  <g key={side}>
                    <circle className="hl-paper" cx={ox + side * (BW * 0.25 + 4)} cy={heldY} r="7" />
                    <circle className="hl" cx={ox + side * (BW * 0.25 + 4)} cy={heldY} r="7" />
                  </g>
                ))}
              </g>

              {MARKS.map((mark, i) => (
                <g
                  key={i}
                  ref={(el) => {
                    tileRefs.current[i] = el;
                  }}
                >
                  <PenCanvas x={-TILE / 2} y={-TILE / 2} s={TILE} mark={mark} />
                </g>
              ))}

              <g ref={doneRef} className="od-done">
                {sparkles.map(([x, y, s]) => (
                  <path
                    key={`${x}-${y}`}
                    className="hl hl-thin od-sparkle"
                    style={{ transformOrigin: `${x}px ${y}px` }}
                    d={`M${x - 10 * s} ${y} H${x + 10 * s} M${x} ${y - 10 * s} V${y + 10 * s} M${x - 5 * s} ${y - 5 * s} L${x + 5 * s} ${y + 5 * s} M${x + 5 * s} ${y - 5 * s} L${x - 5 * s} ${y + 5 * s}`}
                  />
                ))}
              </g>
            </Pen>

            <g className="od-words" style={{ fontSize: layout.font }}>
              <text className="hl-word" x={layout.organiserLabel[0]} y={layout.organiserLabel[1]} textAnchor="middle">
                {t('how.day.organiser')}
              </text>
              <text className="hl-word" x={layout.makersLabel[0]} y={layout.makersLabel[1]} textAnchor="middle">
                {t('how.day.makers')}
              </text>
              <text ref={resultRef} className="hl-word od-result" x={bx + BW / 2} y={by + BH + 46} textAnchor="middle">
                {t('how.day.result')}
              </text>
            </g>

            {/* On a wide screen the stamp sits beside the board, drawn in the
                same picture as the arrow that leads to it, so the two scale
                together and always meet. */}
            {!narrow && (
              <>
                <defs>
                  <filter id="od-rough" x="-10%" y="-10%" width="120%" height="120%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" seed="4" />
                    <feDisplacementMap in="SourceGraphic" scale="6" />
                  </filter>
                </defs>
                <g className="od-next">
                  <Pen>
                    <HandArrow
                      d={`M${bx + BW / 2 + 126} 297 C${bx + BW / 2 + 190} 299, ${STAMP.x - 52} 304, ${STAMP.x - 32} 264`}
                      to={[STAMP.x - 32, 264]}
                      angle={-66}
                    />
                  </Pen>
                  <Link to="/order" className="od-sticker" aria-label={t('home.cta')}>
                    <g transform={`translate(${STAMP.x} ${STAMP.y}) rotate(-5)`}>
                      <rect className="od-sticker-bg" x={-STAMP.w / 2} y={-STAMP.h / 2} width={STAMP.w} height={STAMP.h} filter="url(#od-rough)" />
                      <text className="od-sticker-main" y="-3" textAnchor="middle">
                        {t('home.cta')}
                      </text>
                      <text className="od-sticker-arrow" y="22" textAnchor="middle">
                        &rarr;
                      </text>
                      <text className="od-sticker-side" transform={`translate(${-STAMP.w / 2 + 16} 0) rotate(-90)`} textAnchor="middle">
                        {t('home.hero.stampLeft')}
                      </text>
                      <text className="od-sticker-side" transform={`translate(${STAMP.w / 2 - 16} 0) rotate(90)`} textAnchor="middle">
                        {t('home.hero.stampRight')}
                      </text>
                    </g>
                  </Link>
                </g>
              </>
            )}
          </svg>
          </div>
        </div>
      </section>

      {/* On a phone the stamp comes after the scene rather than inside the
          sticky stage, so the scene can use the whole screen. */}
      {narrow && <Stamp className="od-stamp--below" />}
    </>
  );
}

export default OnTheDay;
